import _ from 'lodash'
import filtersConstants from './constants'
/*
this function is used to generate a payload to render the filters, this transform an object received from backend of this form: 
{
  status: ['open', 'completed'],
  owner: USER_ID
}
to an array of this form: 
[
  {
    key: 'status', 
    value: ['open', 'completed']
  },
  {
    key: 'role',
    value: 'owner'
  },
]
this function needs an object as described above and the array of all available filters to fill some properties to render correctly the filters 
*/

export function deserialize(serializedFilters = {}) {
  return _.compact([
    deserializeConditionsFilter(serializedFilters),
    deserializeApprovalFilter(serializedFilters),
    deserializeValidationsFilter(serializedFilters),
    deserializeRoleFilter(serializedFilters),
    deserializeRoutineActiveFilter(serializedFilters),
    deserializeRoutineTypeFilter(serializedFilters),
    deserializeRoutineDefinitionsPresetFilter(serializedFilters),
    deserializeCreatedByFilter(serializedFilters),
    ...deserializeDateFilters(serializedFilters),
    ...deserializeRegularFilters(serializedFilters),
  ])
}

function deserializeConditionsFilter(serializedFilters) {
  const {
    isHighlighted,
    hasPlannerIntegration,
    isReplica,
    hasReplicas,
    isRevFromFinding,
  } = serializedFilters
  const shouldIncludeConditionFilter =
    _.has(serializedFilters, 'isHighlighted') ||
    _.has(serializedFilters, 'hasPlannerIntegration') ||
    _.has(serializedFilters, 'isReplica') ||
    _.has(serializedFilters, 'hasReplicas') ||
    _.has(serializedFilters, 'isRevFromFinding')

  if (!shouldIncludeConditionFilter) return null

  const response = { key: 'condition', value: [] }

  if (isHighlighted)
    response.value.push(filtersConstants.conditionFilters.highlighted)
  if (hasPlannerIntegration)
    response.value.push(filtersConstants.conditionFilters.hasPlannerIntegration)
  if (isReplica)
    response.value.push(filtersConstants.conditionFilters.isReplica)
  if (hasReplicas)
    response.value.push(filtersConstants.conditionFilters.hasReplicas)
  if (isRevFromFinding === false)
    response.value.push(filtersConstants.conditionFilters.isNotRevFromFinding)
  if (isRevFromFinding)
    response.value.push(filtersConstants.conditionFilters.isRevFromFinding)

  return response
}

function deserializeApprovalFilter(serializedFilters) {
  const { waitingForMyApproval, isRejected, isApproved, isApprovalRequested } = serializedFilters
  const shouldIncludeApprovalFilter =
    _.has(serializedFilters, 'waitingForMyApproval') ||
    _.has(serializedFilters, 'isRejected') ||
    _.has(serializedFilters, 'isApproved') ||
    _.has(serializedFilters, 'isApprovalRequested')

  if (!shouldIncludeApprovalFilter) return null

  const response = { key: 'approval', value: [] }

  if (isApproved)
    response.value.push(filtersConstants.approvalFilters.approved)
  if (isApprovalRequested)
    response.value.push(filtersConstants.approvalFilters.requested)
  if (waitingForMyApproval)
    response.value.push(filtersConstants.approvalFilters.waitingForMyApproval)
  if (isRejected)
    response.value.push(filtersConstants.approvalFilters.rejected)

  return response
}

function deserializeValidationsFilter(serializedFilters) {
  const {
    hasImplementationValidation,
    hasFinancialValidation,
    hasNotImplementationValidation,
  } = serializedFilters
  const shouldIncludeValidationFilter =
    _.has(serializedFilters, 'hasImplementationValidation') ||
    _.has(serializedFilters, 'hasFinancialValidation') ||
    _.has(serializedFilters, 'hasNotImplementationValidation')

  if (!shouldIncludeValidationFilter) return null

  const response = { key: 'validation', value: null }

  if (
    hasImplementationValidation === 'false' ||
    hasImplementationValidation === false ||
    hasNotImplementationValidation
  ) {
    response.value = filtersConstants.validationFilters.notImplemented
  } else if (hasImplementationValidation) {
    response.value = filtersConstants.validationFilters.implemented
  } else if (hasFinancialValidation) {
    response.value = filtersConstants.validationFilters.validated
  }

  return response
}

function deserializeRoleFilter(serializedFilters) {
  const { owner, coach, collaborators } = serializedFilters

  if (!owner && !coach && !collaborators) return null

  return {
    key: 'role',
    value: owner
      ? 'owner'
      : coach
      ? 'coach'
      : collaborators
      ? 'collaborators'
      : null,
  }
}

function deserializeCreatedByFilter(serializedFilters) {
  const { createdBy } = serializedFilters

  if (!createdBy) return null

  return {
    key: 'createdBy',
    value: createdBy,
  }
}

function deserializeRoutineActiveFilter(serializedFilters) {
  if (!_.has(serializedFilters, 'isActive')) return null

  return {
    key: 'activeRoutine',
    value: serializedFilters.isActive ? 'active' : 'inactive',
  }
}

function deserializeRoutineTypeFilter(serializedFilters) {
  const { type } = serializedFilters

  if (!type) return null

  return {
    key: 'routineType',
    value: type,
  }
}

function deserializeRoutineDefinitionsPresetFilter(serializedFilters) {
  const { routineDefinitionsPresetFilter } = serializedFilters

  if (!routineDefinitionsPresetFilter) return null

  return {
    key: 'routineDefinitionsPresetFilter',
    value: routineDefinitionsPresetFilter,
  }
}

function deserializeDateFilters(serializedFilters) {
  const {
    createdAt,
    createdAtOption,
    completedAt,
    completedAtOption,
    dueDate,
    dueDateOption,
  } = serializedFilters
  return _.compact([
    createdAt
      ? deserializeSingleDate('createdAtOption', createdAt, createdAtOption)
      : null,
    completedAt
      ? deserializeSingleDate(
          'completedAtOption',
          completedAt,
          completedAtOption
        )
      : null,
    dueDate
      ? deserializeSingleDate('dueDateOption', dueDate, dueDateOption)
      : null,
  ])
}

function deserializeSingleDate(key, date, dateOption) {
  const dates = _.isArray(date)
    ? date
    : date?.from && date?.to
    ? [date.from, date.to]
    : []
  return {
    key,
    value: dateOption,
    dates: dateOption === 'custom' ? dates : [],
  }
}

function deserializeRegularFilters(serializedFilters) {
  const specialFilters = [
    'createdAt',
    'createdAtOption',
    'completedAt',
    'completedAtOption',
    'dueDate',
    'dueDateOption',
    'isApproved',
    'isHighlighted',
    'hasPlannerIntegration',
    'hasImplementationValidation',
    'hasNotImplementationValidation',
    'hasFinancialValidation',
    'owner',
    'coach',
    'collaborators',
    'createdBy',
    'isActive',
    'type',
    'routineDefinitionsPresetFilter',
    'isReplica',
    'hasReplicas',
    'isRevFromFinding',
    'waitingForMyApproval',
    'isRejected',
    'isApprovalRequested',
  ]

  const regularFilters = _.omit(serializedFilters, specialFilters)

  return Object.keys(regularFilters).map(key => {
    const values = regularFilters[key] ?? []

    return {
      key,
      value: _.isArray(values) ? values : [values], // All the regular filters are presented in multiselects.
    }
  })
}
